import { Button, Dialog, Label, TextField } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import type { IncomeSourceDto } from "../dto.js";

/**
 * "This income stopped" — one half of the distinction the whole module turns on.
 *
 * ==========================================================================================
 * IT ASKS FOR A DATE, AND THE DATE IS THE POINT
 * ==========================================================================================
 *
 * Ending is not a flag. The months up to and including that date keep their income, and every one
 * after it has none — so a job worked through March 2024 leaves 2023 untouched and 2024 correspondingly
 * lower, which is the truth rather than an approximation of it.
 *
 * The copy says so explicitly, because the alternative control is one word away and means the
 * opposite. A member choosing between "It stopped" and "Remove" is choosing what happens to years
 * they have already looked at, and neither label can carry that on its own.
 *
 * A FUTURE DATE IS FINE. A fixed-term contract has one, and refusing it would make somebody wait
 * until the day it ended to record something they already know.
 */
export interface EndSourceDialogProps {
  open: boolean;
  onClose: () => void;
  source: IncomeSourceDto;
  /** Today, from the server's payload — not the browser's clock, which can disagree. */
  today: string;
  onConfirm: (endedOn: string) => void;
  pending: boolean;
  error?: string | null;
}

export function EndSourceDialog({
  open,
  onClose,
  source,
  today,
  onConfirm,
  pending,
  error,
}: EndSourceDialogProps): ReactNode {
  const t = useT();
  const [endedOn, setEndedOn] = useState(today);

  function submit(event: FormEvent): void {
    event.preventDefault();
    onConfirm(endedOn);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`When did ${source.name} stop?`}
      description={t("inc.end.consequence")}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="ended-on">{t("inc.end.lastDay")}</Label>
          <TextField
            id="ended-on"
            type="date"
            value={endedOn}
            onChange={(event) => setEndedOn(event.target.value)}
            required
          />
          {source.startedOn ? (
            <p className="mt-1 text-xs text-muted">
              It started on {source.startedOn}, so it cannot have stopped before then.
            </p>
          ) : null}
        </div>

        {error ? (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {pending ? t("common.saving") : t("inc.end.heading")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
